/**
 * Data layer for `cpa-safeguard-attribution-fraud-protection`. Thin typed fetch wrappers over
 * /api/v1/cpa-safeguard-attribution-fraud-protection (rules + check + verdicts).
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type RuleType =
	| "click_window_max"
	| "click_window_min"
	| "country_mismatch"
	| "ip_mismatch"
	| "device_mismatch"
	| "affiliate_velocity"
	| "self_referral"
	| "payout_cap";

export type Action = "approve" | "review" | "reject";

export type Verdict = "approve" | "review" | "reject";

export interface Rule {
	id: number;
	rule_type: RuleType;
	pattern: string;
	action: Action;
	active: boolean;
	created_at: number;
}

export interface ListRulesResponse {
	rules: Rule[];
}

export interface CreateRuleBody {
	rule_type: RuleType;
	pattern: string;
	action: Action;
}

export interface VerdictRow {
	conversion_id: string;
	verdict: Verdict;
	risk_score: number;
	flags: string[];
	withheld_cents: number;
	payout_amount_cents: number;
	affiliate_id: string | null;
	offer_id: string | null;
	created_at: number;
}

export interface ListVerdictsResponse {
	verdicts: VerdictRow[];
}

const BASE = "/api/v1/cpa-safeguard-attribution-fraud-protection";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function listRules(): Promise<ListRulesResponse> {
	const res = await fetch(`${BASE}/rules`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRulesResponse>(res);
}

export async function createRule(
	body: CreateRuleBody,
): Promise<{ rule: Rule }> {
	const res = await fetch(`${BASE}/rules`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<{ rule: Rule }>(res);
}

export async function deleteRule(id: number): Promise<{ ok: boolean; id: number }> {
	const res = await fetch(`${BASE}/rules/${encodeURIComponent(String(id))}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ ok: boolean; id: number }>(res);
}

export async function listVerdicts(): Promise<ListVerdictsResponse> {
	const res = await fetch(`${BASE}/verdicts`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListVerdictsResponse>(res);
}

export const cpaSafeguardAttributionFraudProtectionQueryKeys = {
	all: ["cpa-safeguard-attribution-fraud-protection"] as const,
	rules: () =>
		[...cpaSafeguardAttributionFraudProtectionQueryKeys.all, "rules"] as const,
	verdicts: () =>
		[...cpaSafeguardAttributionFraudProtectionQueryKeys.all, "verdicts"] as const,
};
